import { useState, useCallback, useMemo } from 'react';
import type { ProjectTask } from '@/types/task';
import { TaskDot } from './TaskDot';
import { ArrowOverlay } from './ArrowOverlay';
import { DrawingCanvas } from './DrawingCanvas';
import { isLineType, lineTypes, type LineType } from '../lib/lineMath';
import type { useContainerDimensions } from '../hooks/useContainerDimensions';
import {
  boxLayout,
  clampToMedia,
  makeGeometry,
  taskShape,
  toPercent,
  type MediaLayout,
  type Point,
  type TaskGeometry,
} from '../lib/annotationGeometry';

export type AnnotationTask = ProjectTask;

/** Data produced when user clicks or draws to create a new task */
export interface NewTaskClickData {
  /** Box percentages of the click/start point, moved onto the picture (positions the form) */
  coordX: number;
  coordY: number;
  /** For video: timestamp in ms */
  time?: number;
  /** Point and line as fractions of the picture: what gets stored */
  geometry: TaskGeometry;
  /** Box pixels of the drawn line (for the preview while the form is open) */
  pixelCoords?: [number, number][];
  /** Line type if drawn (not just clicked) */
  taskType?: LineType;
  /** Container dimensions at time of drawing (for line preview) */
  containerWidth?: number;
  containerHeight?: number;
}

interface AnnotationLayerProps {
  /** Ref to the media container for dimension tracking */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Dimensions from useContainerDimensions */
  dimensions: ReturnType<typeof useContainerDimensions>['dimensions'];
  tasks: AnnotationTask[];
  selectedTask?: AnnotationTask | null;
  onSelectTask: (task: AnnotationTask | null) => void;
  onCreateTask?: (data: NewTaskClickData) => void;
  /** Current playback time in seconds (for video arrow visibility) */
  currentTime?: number;
  /** Whether user can create tasks (false for viewers/guests) */
  canCreate?: boolean;
  /** Media type affects coordinate interpretation */
  mediaType: 'video' | 'image';
  /** Whether the tasks tab is active (annotations only show when active) */
  tasksVisible: boolean;
  /** Where the picture sits in the box; null until its size is known */
  layout: MediaLayout | null;
}

export function AnnotationLayer({
  containerRef,
  dimensions,
  tasks,
  selectedTask,
  onSelectTask,
  onCreateTask,
  currentTime,
  canCreate = false,
  mediaType,
  tasksVisible,
  layout,
}: AnnotationLayerProps) {
  const [lineType, setLineType] = useState<LineType>(lineTypes.freeLine);
  const [isDrawing, setIsDrawing] = useState(false);

  // Until the picture's size is known, treat the whole box as the picture
  const effectiveLayout = useMemo(
    () => layout ?? (dimensions.width ? boxLayout({ width: dimensions.width, height: dimensions.height }) : null),
    [layout, dimensions.width, dimensions.height],
  );

  const handleClick = useCallback(
    (pixelX: number, pixelY: number) => {
      if (!onCreateTask || !effectiveLayout) return;
      const pt = clampToMedia([pixelX, pixelY], effectiveLayout);
      const { x, y } = toPercent(pt, effectiveLayout);
      onCreateTask({ coordX: x, coordY: y, geometry: makeGeometry(pt, null, effectiveLayout) });
    },
    [onCreateTask, effectiveLayout],
  );

  const handleDrawStart = useCallback(() => {
    setIsDrawing(true);
  }, []);

  const handleDrawEnd = useCallback(
    (coords: [number, number][]) => {
      setIsDrawing(false);
      if (!onCreateTask || !effectiveLayout || coords.length < 2) return;
      // Keep the line on the picture, so the preview matches what is stored
      const line = coords.map((p) => clampToMedia(p, effectiveLayout));
      const { x, y } = toPercent(line[0], effectiveLayout);
      onCreateTask({
        coordX: x,
        coordY: y,
        geometry: makeGeometry(line[0], line, effectiveLayout),
        pixelCoords: line,
        taskType: lineType,
        containerWidth: effectiveLayout.box.width,
        containerHeight: effectiveLayout.box.height,
      });
    },
    [onCreateTask, effectiveLayout, lineType],
  );

  const shapes = useMemo(() => {
    const out = new Map<string, { pt: Point | null; line: Point[] | null }>();
    if (!effectiveLayout) return out;
    for (const task of tasks) out.set(task.id, taskShape(task, effectiveLayout, mediaType));
    return out;
  }, [tasks, effectiveLayout, mediaType]);

  if (!tasksVisible || !effectiveLayout) return null;

  return (
    <>
      {/* Drawing canvas for creating new tasks */}
      {canCreate && onCreateTask && (
        <DrawingCanvas
          containerRef={containerRef}
          onDrawEnd={handleDrawEnd}
          onDrawStart={handleDrawStart}
          onClick={handleClick}
        />
      )}

      {/* Task dots */}
      {tasks.map((task) => {
        const pt = shapes.get(task.id)?.pt;
        if (!pt) return null;
        const { x, y } = toPercent(pt, effectiveLayout);

        // For video arrow/line tasks: only show dot during timestamp+duration window
        if (
          mediaType === 'video' &&
          currentTime !== undefined &&
          task.timestamp != null &&
          isLineType(task.taskType)
        ) {
          const startSec = Number(task.timestamp) / 1000;
          const endSec = startSec + Number(task.duration ?? 1);
          if (currentTime < startSec - 0.1 || currentTime > endSec + 0.1) return null;
        }

        // For video static (non-line) tasks: the overlay dot would otherwise sit on
        // the video for the whole playback — show it only while the task is selected;
        // the timeline marker remains the always-visible handle for these
        if (mediaType === 'video' && !isLineType(task.taskType) && selectedTask?.id !== task.id) {
          return null;
        }

        return (
          <TaskDot
            key={task.id}
            coordX={x}
            coordY={y}
            taskType={task.taskType}
            isSelected={selectedTask?.id === task.id}
            onClick={() => onSelectTask(task)}
            pointerEventsNone={isDrawing}
          />
        );
      })}

      {/* Arrow/line overlays */}
      {tasks
        .filter((t) => isLineType(t.taskType) && shapes.get(t.id)?.line)
        .map((task) => {
          // For video: only show arrow if current time is within range
          let visible = true;
          if (mediaType === 'video' && currentTime !== undefined && task.timestamp != null) {
            const startSec = Number(task.timestamp) / 1000;
            const endSec = startSec + Number(task.duration ?? 1);
            visible = currentTime >= startSec - 0.1 && currentTime <= endSec + 0.1;
          }

          return (
            <ArrowOverlay
              key={`arrow-${task.id}`}
              coords={shapes.get(task.id)!.line!}
              type={task.taskType as LineType}
              viewBoxWidth={effectiveLayout.box.width}
              viewBoxHeight={effectiveLayout.box.height}
              visible={visible}
            />
          );
        })}
    </>
  );
}
